import { Form, router } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { type ReactNode, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { toMessages } from "@/lib/form-errors"
import { adminBrandAcquisitionResetPath } from "@/routes"
import type { ResetAcquisitionPreview } from "@/types/admin"

const COUNT_KEYS = [
  "candidates",
  "crew_members",
  "collabs",
  "submissions",
  "metrics_snapshots",
  "notifications",
] as const

interface ResetAcquisitionDialogProps {
  brandId: number
  brandName: string
  /** Deferred `reset_preview` page prop — `undefined` until fetched. */
  preview: ResetAcquisitionPreview | undefined
  online: boolean
}

/**
 * OKS-514 — Destructive "Reset acquisition" confirm.
 *
 * shadcn `AlertDialog` (NOT shadcn `Form` — Crew rule). Opening the dialog
 * lazily fetches the counts preview via `router.reload({ only:
 * ["reset_preview"] })` (the `reset_preview` Inertia `defer` prop). States
 * per UX Spec §3: loading (skeleton rows), error (inline Alert + Retry),
 * idle (counts + type-to-confirm), submitting (spinner, non-dismissible).
 *
 * The type-the-name guard enables the destructive button only on a trimmed,
 * case-insensitive match; the server re-checks the same (KD-5), so this is
 * UX, not the security boundary. The confirm input is a plain shadcn
 * `Input` + `Label` (`name="confirmation_name"`) inside an Inertia `<Form>`
 * that POSTs to the reset endpoint. No react-hook-form, no zod.
 *
 * Locked testids: `admin-reset-acquisition-button` (trigger),
 * `admin-reset-acquisition-dialog` (content),
 * `admin-reset-acquisition-confirm` (destructive submit).
 */
export function ResetAcquisitionDialog({
  brandId,
  brandName,
  preview,
  online,
}: ResetAcquisitionDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [typedName, setTypedName] = useState("")
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  // Mirror the Inertia `<Form>` `processing` flag for `onOpenChange`, which
  // fires outside the render-prop scope (mid-submit close guard).
  const submittingRef = useRef(false)

  const nameMatches =
    brandName.trim().length > 0 &&
    typedName.trim().toLowerCase() === brandName.trim().toLowerCase()

  function fetchPreview() {
    setLoadError(false)
    setLoading(true)
    router.reload({
      only: ["reset_preview"],
      onSuccess: () => setLoading(false),
      onError: () => {
        setLoading(false)
        setLoadError(true)
      },
    })
  }

  // Lazily fetch the counts when the dialog opens (re-fetch on every open so
  // the preview reflects current state). Driven from the open handler rather
  // than an effect to keep the deferred-prop reload an explicit user action.
  function handleOpen() {
    setOpen(true)
    fetchPreview()
  }

  function handleClose() {
    setOpen(false)
    setTypedName("")
    setLoadError(false)
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Block close (Esc) while the purge is in flight.
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else handleOpen()
      }}
    >
      <Button
        type="button"
        variant="destructive"
        disabled={!online}
        aria-disabled={!online ? "true" : undefined}
        onClick={handleOpen}
        data-testid="admin-reset-acquisition-button"
      >
        {t("admin.brand_detail.reset.button")}
      </Button>
      <AlertDialogContent
        className="sm:max-w-lg"
        data-testid="admin-reset-acquisition-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("admin.brand_detail.reset.dialog.title", { brand: brandName })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("admin.brand_detail.reset.dialog.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Counts preview: loading → skeleton, error → Alert+Retry, else list */}
        {loadError ? (
          <Alert variant="destructive" data-testid="admin-reset-preview-error">
            <AlertTitle>
              {t("admin.brand_detail.reset.dialog.error_title")}
            </AlertTitle>
            <AlertDescription>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={fetchPreview}
              >
                {t("admin.brand_detail.reset.dialog.retry")}
              </Button>
            </AlertDescription>
          </Alert>
        ) : !preview ? (
          <div
            className="flex flex-col gap-2"
            aria-busy={loading || !preview ? "true" : undefined}
            data-testid="admin-reset-preview-loading"
          >
            <p className="text-muted-foreground text-sm">
              {t("admin.brand_detail.reset.dialog.loading")}
            </p>
            {COUNT_KEYS.map((key) => (
              <Skeleton key={key} className="h-5 w-full" />
            ))}
          </div>
        ) : (
          <div
            className="flex flex-col gap-3"
            data-testid="admin-reset-preview-counts"
          >
            <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("admin.brand_detail.reset.dialog.counts_title")}
            </h3>
            <dl className="grid gap-1.5 text-sm">
              {COUNT_KEYS.map((key) => (
                <div key={key} className="flex items-center justify-between">
                  <dt className="text-muted-foreground">
                    {t(`admin.brand_detail.reset.dialog.counts.${key}`)}
                  </dt>
                  <dd className="text-foreground font-medium tabular-nums">
                    {preview[key].toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm">
              <BoldText
                text={t("admin.brand_detail.reset.dialog.accounts", {
                  deleted: preview.accounts_deleted.toLocaleString(),
                  detached: preview.accounts_detached.toLocaleString(),
                })}
              />
            </p>
          </div>
        )}

        <Form
          action={adminBrandAcquisitionResetPath(brandId)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          className="flex flex-col gap-4"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            const fieldErrors = toMessages(
              errors.confirmation_name as string | string[] | undefined,
            )
            return (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="reset-confirm-name">
                    <BoldText
                      text={t("admin.brand_detail.reset.dialog.confirm_label", {
                        brand: brandName,
                      })}
                    />
                  </Label>
                  <Input
                    id="reset-confirm-name"
                    name="confirmation_name"
                    autoComplete="off"
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    disabled={processing}
                    aria-invalid={fieldErrors ? "true" : undefined}
                    aria-describedby="reset-confirm-helper"
                  />
                  <p
                    id="reset-confirm-helper"
                    className="text-muted-foreground text-xs"
                  >
                    {t("admin.brand_detail.reset.dialog.confirm_helper")}
                  </p>
                  <InputError messages={fieldErrors} />
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("admin.brand_detail.reset.dialog.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={!nameMatches || processing || !online}
                    aria-disabled={
                      !nameMatches || processing || !online ? "true" : undefined
                    }
                    data-testid="admin-reset-acquisition-confirm"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("admin.brand_detail.reset.dialog.submitting")
                      : t("admin.brand_detail.reset.dialog.submit")}
                  </Button>
                </div>
              </>
            )
          }}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * Renders a string containing `**bold**` markdown segments, wrapping each in
 * `<strong>`. Mirrors the split-on-`**` approach used by the agency
 * replace-warning — i18next can't safely emit tags via interpolation.
 */
function BoldText({ text }: { text: string }): ReactNode {
  const segments = text.split("**")
  return (
    <>
      {segments.map((segment, index) =>
        index % 2 === 1 ? (
          <strong key={index}>{segment}</strong>
        ) : (
          <span key={index}>{segment}</span>
        ),
      )}
    </>
  )
}
